import type { Uuid } from '@shared/api';
import type { MediaLink } from '@entities/media';
import type { ManufacturerBrief } from '@entities/manufacturer';
import type { UserBasicInfo } from '@entities/user-directory';

export type MessageStatus = 'SENT' | 'DELIVERED' | 'READ';

export interface MessageView {
  id: Uuid;
  conversationId: Uuid;
  senderId: Uuid;
  body: string;
  attachments: MediaLink[];
  status: MessageStatus;
  createdAt: string;
}

export interface ConversationView {
  id: Uuid;
  buyerId: Uuid;
  producerId: Uuid;
  buyer: UserBasicInfo | null;
  producer: ManufacturerBrief | null;
  unreadCount: number;
  /** Last message body, trimmed to 140 chars on the server (with ellipsis). */
  lastMessagePreview: string | null;
  /** Sender of the last message — compare with current user to render "You: …". */
  lastMessageSenderId: Uuid | null;
  /** True when the last message carried any attachments → render a 📎 hint. */
  lastMessageHasAttachments: boolean;
  lastMessageAt: string | null;
  counterpartOnline: boolean;
}

export interface CounterpartDisplay {
  id: Uuid;
  name: string;
  initials: string;
  avatarUrl: string | null;
  /** Optional sub-line — country flag, KYC chip, etc. */
  subtitle: string | null;
}

/**
 * Pick the counterpart side of a conversation relative to the current user.
 * Vendor admin = producer side, so the counterpart is always the buyer; but
 * we still derive it generically in case the same code is shared with other
 * contours later.
 */
export function pickCounterpart(
  conv: Pick<ConversationView, 'buyerId' | 'producerId' | 'buyer' | 'producer'>,
  ownUserId: string | null | undefined,
): CounterpartDisplay {
  const viewingAsBuyer = ownUserId != null && ownUserId === conv.buyerId;
  if (viewingAsBuyer && conv.producer) {
    const p = conv.producer;
    return {
      id: p.id,
      name: p.companyName,
      initials: (p.companyName || '?').trim().slice(0, 2).toUpperCase(),
      avatarUrl: p.logo?.url ?? null,
      subtitle: p.country,
    };
  }
  if (conv.buyer) {
    const b = conv.buyer;
    return {
      id: b.id,
      name: b.displayName,
      initials: b.initials || (b.displayName || '?').trim().slice(0, 2).toUpperCase(),
      avatarUrl: null,
      subtitle: null,
    };
  }
  const fallbackId = viewingAsBuyer ? conv.producerId : conv.buyerId;
  return {
    id: fallbackId,
    name: `User ${fallbackId.slice(0, 8)}`,
    initials: fallbackId.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase(),
    avatarUrl: null,
    subtitle: null,
  };
}

export interface SendMessageRequest {
  body: string;
  attachments: Uuid[];
}

export type ConversationEvent =
  | { event: 'message.new'; message: MessageView }
  | { event: 'message.status'; messageId: Uuid; status: MessageStatus };

export interface InboxEvent {
  conversationId: Uuid;
  message: MessageView;
}
